import { FC } from 'react';
import { Link } from 'react-router-dom';
import { InvestorProfile } from '@/types/index';

interface Props {
  profile: Partial<InvestorProfile>;
}

function detectProfil(p: Partial<InvestorProfile>): { label: string; icon: string; color: string; desc: string } {
  const tol = p.toleranceRisque ?? 3;
  const obj = p.objectifPrincipal;

  if (tol <= 2 && !p.accepteTravaux && obj === 'SECURITE') {
    return { label: 'Investisseur prudent', icon: '🛡️', color: 'text-emerald-700', desc: 'Stabilité et sécurité avant tout. Préfère les biens sans travaux, loués, avec adresse complète.' };
  }
  if (tol >= 4 && p.accepteTravaux && obj === 'CAPITAL') {
    return { label: 'Investisseur dynamique', icon: '🚀', color: 'text-orange-600', desc: 'Recherche activement la valeur ajoutée. Accepte les risques pour maximiser la plus-value.' };
  }
  if (obj === 'CASHFLOW' && (p.cashflowCible ?? 0) > 500) {
    return { label: 'Optimiseur de rendement', icon: '💸', color: 'text-blue-600', desc: 'Focus sur le cash flow mensuel. Recherche les rendements élevés, levier financier fort.' };
  }
  if (tol >= 4 && p.strategies && (p.strategies as string[]).includes('COMMERCIAL')) {
    return { label: 'Investisseur opportuniste', icon: '🎯', color: 'text-purple-600', desc: 'Ouvert à tous types de biens. Saisi les opportunités cross-sectorielles.' };
  }
  return { label: 'Investisseur équilibré', icon: '⚖️', color: 'text-gray-700', desc: 'Approche mixte entre rendement et sécurité. Profil polyvalent adapté aux différents marchés.' };
}

function fmt(val: number | null | undefined, unit = '') {
  if (val == null) return '—';
  return `${val.toLocaleString('de-CH')}${unit}`;
}

export const Step7Resume: FC<Props> = ({ profile }) => {
  const profil = detectProfil(profile);

  return (
    <div className="space-y-6">
      <div className={`p-5 rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700`}>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">{profil.icon}</span>
          <div>
            <h3 className={`text-lg font-bold ${profil.color}`}>{profil.label}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{profil.desc}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {profile.objectifPrincipal && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-400">Objectif</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{profile.objectifPrincipal}</p>
          </div>
        )}
        {profile.horizon && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-400">Horizon</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{profile.horizon}</p>
          </div>
        )}
        {profile.fondsPropresCHF && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-400">Fonds propres</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">CHF {fmt(profile.fondsPropresCHF)}</p>
          </div>
        )}
        {profile.equityRate && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-400">Apport</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{profile.equityRate}%</p>
          </div>
        )}
        {profile.rejectionMinYield != null && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-400">Rendement min rejet</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{profile.rejectionMinYield}%</p>
          </div>
        )}
        {profile.cashflowMin != null && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-400">Cash flow min</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">CHF {fmt(profile.cashflowMin)}/mois</p>
          </div>
        )}
        {profile.toleranceRisque && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-400">Tolérance risque</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{profile.toleranceRisque}/5</p>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
            Profil sauvegardé — appliquez-le au catalogue
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
            Le bouton <strong>"Reclassifier selon mon profil"</strong> dans les Annonces en vrac calcule le score de chaque bien et met à jour les catégories (Opportunité / Rejeté).
          </p>
          <Link
            to="/listings"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Aller aux annonces →
          </Link>
        </div>
      </div>
    </div>
  );
};
